package dev.lingvo.site.components.editor

import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.lingvo.shared.MAX_REVIEW_BATCH
import dev.lingvo.shared.MtEstimateDto
import dev.lingvo.shared.MtJobDto
import dev.lingvo.shared.MtJobState
import dev.lingvo.shared.MtPretranslateRequest
import dev.lingvo.shared.ReviewAction
import dev.lingvo.shared.ReviewBatchItem
import dev.lingvo.shared.ReviewBatchOutcome
import dev.lingvo.shared.ReviewBatchRequest
import dev.lingvo.shared.SourceReviewBatchItem
import dev.lingvo.shared.SourceReviewBatchRequest
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.ToastState
import dev.lingvo.site.i18n.tr
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.launch

/** Which queue-scoped automation the bulk confirm is currently about. */
internal enum class EditorBulkIntent { PRETRANSLATE, APPROVE_CLEAN }

/**
 * What the confirm is showing right now.
 *
 * The estimate and the preview are both loaded *after* the dialog opens rather than before the
 * button is pressed, because both are requests: pricing every keystroke of a filter change would
 * be a spend query per render. [error] is the refusal the server gave for that request; it is shown
 * in the dialog beside a disabled confirm rather than as a toast, because the reader is standing in
 * front of the decision it belongs to.
 */
internal data class EditorBulkConfirmation(
    val intent: EditorBulkIntent,
    val count: Int,
    val scopeSentence: String,
    val previewNames: List<String> = emptyList(),
    val estimate: MtEstimateDto? = null,
    val request: MtPretranslateRequest? = null,
    val items: List<ReviewBatchItem> = emptyList(),
    val sourceItems: List<SourceReviewBatchItem>? = null,
    val loading: Boolean = true,
    val error: String? = null,
    val running: Boolean = false,
)

/**
 * The queue header's two automations, and the one run either of them can have in flight.
 *
 * **The initiating control is the only thing that goes busy.** Nothing here clears `rows`, blanks
 * the header or unmounts the queue: a run's progress is a readout beside the counts, and the reload
 * at the end goes through `loadKeys`, which restores from cache and replaces in place. That is the
 * retained-continuity rule, and the editor is the surface where breaking it costs somebody the
 * sentence they were half-way through typing.
 *
 * **Estimate before, receipt after.** The pre-translate confirm prices the exact
 * [MtPretranslateRequest] the confirm button will post — not a request rebuilt from the same
 * arguments a second time — so the run that is priced and the run that starts cannot drift apart.
 * The approve confirm is built from a `dryRun` `decide-batch`, which is the same code path, the
 * same gate and the same per-item answers as the write.
 */
@Stable
internal class EditorQueueAutomationStore(
    private val projectSlug: String,
    private val locale: String,
    private val scope: CoroutineScope,
    private val toasts: ToastState,
    private val onReload: () -> Unit,
    private val onOpenReceipt: (String) -> Unit,
    private val onShowKeys: (List<String>) -> Unit,
) {
    var confirmation by mutableStateOf<EditorBulkConfirmation?>(null)
        private set

    /** The run this page started, while it is running and once more when it finishes. */
    var job by mutableStateOf<MtJobDto?>(null)
        private set

    /** True from the press until the job id comes back, so only the pressed button goes busy. */
    var starting by mutableStateOf(false)
        private set

    private var pollJob: Job? = null

    val runningJobId: String?
        get() = job?.takeIf { it.state == MtJobState.RUNNING }?.id

    fun openPretranslate(scopeSentence: String, plan: EditorPretranslateScope) {
        confirmation = EditorBulkConfirmation(
            intent = EditorBulkIntent.PRETRANSLATE,
            count = plan.untranslated,
            scopeSentence = scopeSentence,
            request = plan.request,
        )
        scope.launch {
            val estimate = try {
                ApiClient.mtEstimate(projectSlug, plan.request)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                updateConfirmation(EditorBulkIntent.PRETRANSLATE) {
                    it.copy(loading = false, error = refusal(failure))
                }
                return@launch
            }
            updateConfirmation(EditorBulkIntent.PRETRANSLATE) {
                it.copy(loading = false, estimate = estimate, error = estimate.blockedReason)
            }
        }
    }

    fun openApproveClean(scopeSentence: String, items: List<ReviewBatchItem>, names: List<String>, sourceItems: List<SourceReviewBatchItem>? = null) {
        confirmation = EditorBulkConfirmation(
            intent = EditorBulkIntent.APPROVE_CLEAN,
            count = items.size,
            scopeSentence = scopeSentence,
            previewNames = names,
            items = items,
            sourceItems = sourceItems,
        )
        scope.launch {
            // **The preview covers every chunk the confirm button will send.** It used to dry-run
            // `items.take(MAX_REVIEW_BATCH)` and then title the dialog from that one answer, while
            // `confirmApproveClean` looped over the *whole* list — so a reader who had paged 260
            // rows into the queue pressed "Approve 200 strings" and approved 260. `rows` grows by
            // `EDITOR_PAGE_SIZE` with every `loadMore`, so passing 200 is ordinary, not exotic.
            // The dry run is chunked exactly as the write is, and the count is the sum, so the
            // number in the title is the number the run decides.
            var previewed = 0
            for (chunk in items.chunked(MAX_REVIEW_BATCH)) {
                val preview = try {
                    approveChunk(chunk, sourceItems, dryRun = true)
                } catch (cancelled: CancellationException) {
                    throw cancelled
                } catch (failure: Throwable) {
                    updateConfirmation(EditorBulkIntent.APPROVE_CLEAN) {
                        it.copy(loading = false, error = refusal(failure))
                    }
                    return@launch
                }
                previewed += preview.decided
                if (confirmation?.intent != EditorBulkIntent.APPROVE_CLEAN) return@launch
            }
            updateConfirmation(EditorBulkIntent.APPROVE_CLEAN) {
                it.copy(loading = false, count = previewed)
            }
        }
    }

    /**
     * Rewrites the confirm only while it is still the one [intent] opened.
     *
     * **A guard that nulls the dialog it was guarding is not a guard.** Every one of these writes
     * used to read `confirmation = confirmation?.takeIf { … }?.copy(…)`, and Kotlin evaluates that
     * whole chain to `null` when the `takeIf` misses — so a request that landed after the reader
     * had cancelled and opened the *other* automation did not paint its refusal into that dialog,
     * it closed it. Which is the same defect wearing a quieter face: the reader loses the thing
     * they are looking at because of a request they already walked away from.
     *
     * Returning null from [update] is how a path deliberately closes its own confirm, and it can
     * only ever close its own.
     */
    private fun updateConfirmation(
        intent: EditorBulkIntent,
        update: (EditorBulkConfirmation) -> EditorBulkConfirmation?,
    ) {
        val current = confirmation?.takeIf { it.intent == intent } ?: return
        confirmation = update(current)
    }

    fun dismissConfirmation() {
        confirmation = null
    }

    /** Starts the priced run, or attaches to the one already going. */
    fun confirmPretranslate() {
        val pending = confirmation?.takeIf { it.intent == EditorBulkIntent.PRETRANSLATE } ?: return
        val request = pending.request ?: return
        if (pending.running || starting) return
        confirmation = pending.copy(running = true, error = null)
        starting = true
        scope.launch {
            val jobId = try {
                ApiClient.pretranslate(projectSlug, request)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                // A run is already going for this project. `details.jobId` names it, and attaching
                // is what the reader wanted anyway — refusing here would leave the page with no
                // progress readout for work that is genuinely under way.
                val existing = (failure as? ApiException)
                    ?.takeIf { it.status == 409 && it.code == "job_already_running" }
                    ?.details?.get("jobId")?.toString()?.trim('"')
                if (existing.isNullOrBlank()) {
                    // Guarded by intent, through the one helper that cannot touch another
                    // automation's dialog. Without it a slow pre-translate that failed after the
                    // reader had cancelled and opened "Approve QA-clean" painted "No provider is
                    // connected" into the approve dialog.
                    updateConfirmation(EditorBulkIntent.PRETRANSLATE) {
                        it.copy(running = false, error = refusal(failure))
                    }
                    starting = false
                    return@launch
                }
                existing
            }
            updateConfirmation(EditorBulkIntent.PRETRANSLATE) { null }
            starting = false
            attach(jobId)
        }
    }

    fun confirmApproveClean() {
        val pending = confirmation?.takeIf { it.intent == EditorBulkIntent.APPROVE_CLEAN } ?: return
        if (pending.running || pending.items.isEmpty()) return
        confirmation = pending.copy(running = true, error = null)
        scope.launch {
            var decided = 0
            val outcomes = mutableListOf<ReviewBatchOutcome>()
            for (chunk in pending.items.chunked(MAX_REVIEW_BATCH)) {
                val response = try {
                    approveChunk(chunk, pending.sourceItems, dryRun = false)
                } catch (cancelled: CancellationException) {
                    throw cancelled
                } catch (failure: Throwable) {
                    val reason = refusal(failure)
                    if (outcomes.isEmpty()) {
                        // Nothing reached the database, so the dialog stays open with the reason
                        // and pressing again is the retry.
                        updateConfirmation(EditorBulkIntent.APPROVE_CLEAN) {
                            it.copy(running = false, error = reason)
                        }
                        return@launch
                    }
                    // **Earlier chunks are already written.** Reporting a total refusal and
                    // skipping the reload left 200 approved strings in the database while the queue
                    // behind the dialog still listed them as pending — and pressing again re-sent
                    // them, to come back ALREADY_REVIEWED. The receipt for what did happen is owed
                    // to the reader even when the run did not finish.
                    updateConfirmation(EditorBulkIntent.APPROVE_CLEAN) { null }
                    onReload()
                    showApproveReceipt(
                        outcomes = outcomes,
                        decided = decided,
                        unsent = pending.items.size - outcomes.size,
                        reason = reason,
                    )
                    return@launch
                }
                decided += response.decided
                outcomes += response.outcomes
            }
            updateConfirmation(EditorBulkIntent.APPROVE_CLEAN) { null }
            onReload()
            showApproveReceipt(outcomes = outcomes, decided = decided, unsent = 0, reason = null)
        }
    }

    private suspend fun approveChunk(items: List<ReviewBatchItem>, captured: List<SourceReviewBatchItem>?, dryRun: Boolean) =
        if (captured == null) ApiClient.reviewDecideBatch(projectSlug, locale, ReviewBatchRequest(items, ReviewAction.APPROVE, dryRun = dryRun))
        else {
            if (!Session.serverFeatures.sourceAwareReview) throw unavailableSourceReview()
            val ids = items.mapTo(mutableSetOf()) { it.keyId }
            val sourceItems = captured.filter { it.keyId in ids }
            if (sourceItems.size != items.size) throw unavailableSourceReview()
            ApiClient.reviewDecideBatchForSource(projectSlug, locale, SourceReviewBatchRequest(sourceItems, ReviewAction.APPROVE, dryRun = dryRun))
        }

    /**
     * The receipt, and the "Show skipped" action when there is anything to show.
     *
     * One place rather than two, because the partial path — a chunk that failed after earlier
     * chunks committed — owes the reader exactly the same accounting as the whole one, plus the
     * sentence saying what did not get sent.
     */
    private fun showApproveReceipt(
        outcomes: List<ReviewBatchOutcome>,
        decided: Int,
        unsent: Int,
        reason: String?,
    ) {
        val skipped = editorSkippedKeyIds(outcomes)
        val receipt = editorApproveReceipt(outcomes, decided)
        val message = if (reason == null) {
            receipt
        } else {
            editorPartialApproveReceipt(receipt, unsent, reason)
        }
        val variant = when {
            reason != null -> LToastVariant.DANGER
            skipped.isEmpty() -> LToastVariant.SUCCESS
            else -> LToastVariant.WARNING
        }
        if (skipped.isEmpty()) {
            toasts.show(message, variant)
        } else {
            toasts.showWithAction(
                message,
                actionLabel = tr("editor.editor_queue_automation_store.show_skipped", "Show skipped"),
                onAction = { onShowKeys(skipped) },
                variant = variant,
            )
        }
    }

    /** Follows a run to its end, then reloads the queue in place and offers the receipt. */
    fun attach(jobId: String) {
        pollJob?.cancel()
        pollJob = scope.launch {
            try {
                ApiClient.pollJob(projectSlug, jobId).collect { latest -> job = latest }
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                toasts.show(refusal(failure), LToastVariant.DANGER)
                return@launch
            }
            val finished = job ?: return@launch
            onReload()
            toasts.showWithAction(
                editorJobReceipt(finished),
                actionLabel = tr("editor.editor_queue_automation_store.open_receipt", "Open receipt"),
                onAction = { onOpenReceipt(finished.id) },
                variant = if (finished.state == MtJobState.FAILED) {
                    LToastVariant.DANGER
                } else {
                    LToastVariant.SUCCESS
                },
            )
        }
    }

    fun dispose() {
        pollJob?.cancel()
        pollJob = null
    }

    private fun refusal(failure: Throwable): String = failure.message?.takeIf(String::isNotBlank)
        ?: tr(
            "editor.editor_queue_automation_store.that_did_not_go_through",
            "That did not go through. Try it again.",
        )
}
